import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import { useTranslation } from 'react-i18next';
import { getInitials } from '../editor/features/roles/utils/rolColorUtils';
import { prepareRoleColorForChat } from '../../utils/roleColorValidation';
import AgentDraftBadge from '../explore/AgentDraftBadge';
import { formatRelativeDraft } from '../explore/AgentDraftBadge';

/**
 * AgentPreviewCard — Compact hover card shown when hovering an agent item.
 * Used in both the sidebar (SidebarAgentsList) and the Explore grid (AgentCard).
 * Purely presentational; callers control visibility and anchoring via Popper.
 *
 * @param {object}   role      - Role object from useRoles()
 * @param {boolean}  isHidden  - Whether this role is hidden from the sidebar
 * @param {function} onChat    - Start-new-chat handler
 * @param {function} onEdit    - Open editor handler
 * @param {object}   [sx]      - Extra sx forwarded to the Paper root
 */
const AgentPreviewCard = ({ role, isHidden = false, onChat, onEdit, sx }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  if (!role) return null;

  const rc = prepareRoleColorForChat(role.color, theme);
  const hasBadges = role.isSystem || isHidden || role.hasDraft;
  const editedRelative = formatRelativeDraft(role.updated_at, t);
  const accentColor = role.color || theme.palette.primary.main;

  return (
    <Paper
      elevation={8}
      data-testid="agent-preview-card"
      sx={{
        width: 300,
        borderRadius: 2,
        overflow: 'hidden',
        pointerEvents: 'auto',
        outline: `1px solid ${alpha(accentColor, 0.2)}`,
        ...sx,
      }}
    >
      {/* Tinted header — gives hover card a distinct visual identity vs the card below */}
      <Box sx={{ px: 2, pt: 2, pb: 1.5, bgcolor: alpha(accentColor, 0.07) }}>
        {/* Header: avatar + name */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: hasBadges ? 1.25 : 1 }}>
          <Avatar
            sx={{
              width: 44,
              height: 44,
              fontSize: '1rem',
              fontWeight: 700,
              background: rc.avatarBgGradient,
              boxShadow: rc.avatarShadow,
              color: rc.avatarText,
              flexShrink: 0,
            }}
          >
            {getInitials(role.name)}
          </Avatar>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, lineHeight: 1.3, wordBreak: 'break-word' }}
          >
            {role.name}
          </Typography>
        </Box>

        {/* Badges */}
        {hasBadges && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.25 }}>
            {role.isSystem && (
              <Tooltip
                title={t('agents.systemTooltip', 'Agente del sistema — puedes personalizar instrucciones y objetivos')}
                arrow
                placement="top"
              >
                <Chip
                  icon={<ShieldOutlinedIcon sx={{ fontSize: '0.75rem' }} />}
                  label={t('agents.systemBadge', 'Sistema')}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    color: 'text.secondary',
                    borderColor: 'divider',
                    '& .MuiChip-icon': { color: 'text.secondary' },
                  }}
                />
              </Tooltip>
            )}
            {isHidden && (
              <Tooltip
                title={t('explore.hiddenTooltip', 'No aparece en el sidebar')}
                arrow
                placement="top"
              >
                <Chip
                  icon={<VisibilityOffIcon sx={{ fontSize: '0.75rem' }} />}
                  label={t('explore.hiddenBadge', 'Oculto')}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    color: 'text.secondary',
                    borderColor: 'divider',
                    '& .MuiChip-icon': { color: 'text.secondary' },
                  }}
                />
              </Tooltip>
            )}
            <AgentDraftBadge hasDraft={role.hasDraft} draftUpdatedAt={role.draftUpdatedAt} />
          </Box>
        )}

      </Box>

      {/* White zone: description + metadata */}
      <Box sx={{ px: 2, pt: 1.25, pb: 1.5 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            maxHeight: 120,
            overflowY: 'auto',
            lineHeight: 1.55,
            fontSize: '0.8rem',
            mb: 1,
            pr: 0.5,
          }}
        >
          {role.objectives || t('explore.detail.noDescription', 'Sin descripción')}
        </Typography>

        {editedRelative && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: 'block', fontSize: '0.7rem' }}
          >
            {t('sidebar.hoverCard.edited', 'Editado {{relative}}', { relative: editedRelative })}
          </Typography>
        )}
      </Box>

      <Divider />

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, p: 1.25 }}>
        <Button
          size="small"
          variant="contained"
          disableElevation
          startIcon={<AddCommentOutlinedIcon sx={{ fontSize: '0.9rem' }} />}
          onClick={onChat}
          aria-label={t('sidebar.agentMenu.newChat', 'Iniciar un chat')}
          sx={{
            flex: 1,
            fontSize: '0.75rem',
            textTransform: 'none',
            py: 0.5,
          }}
        >
          {t('sidebar.agentMenu.newChat', 'Iniciar un chat')}
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<TuneIcon sx={{ fontSize: '0.9rem' }} />}
          onClick={onEdit}
          aria-label={t('explore.actions.edit', 'Editar')}
          sx={{
            flex: 1,
            fontSize: '0.75rem',
            textTransform: 'none',
            py: 0.5,
          }}
        >
          {t('explore.actions.edit', 'Editar')}
        </Button>
      </Box>
    </Paper>
  );
};

export default AgentPreviewCard;
